// 檔案：ui/modules/legal/forms/ContractForm.tsx
import React, { useState, useEffect } from "react";
import { Modal } from "../../../components/Modal";
import { FileUploadKit } from "../../../components/common/FileUploadKit";
import { useAuth } from "../../../context/AuthContext";
import { fetchNextContractNo } from "../../../utils/idGenerator";
import { db, serverTimestamp } from "../../../../firebaseClient";
import { formatDate, parseDate } from "../../../utils/dateUtils";
import { useToast } from "../../../components/Toast";
import { AIPlaceholderButton } from "../../../components/ai/AIPlaceholderButton";
import { CONTRACT_TYPE_OPTIONS } from "../../../constants/contractTypes";
import type { ContractDoc } from "../../../types/legalOps";
import { uploadContractFiles } from "../../../services/storageUpload";
import { DateField } from "../../../components/form/DateField";
import { syncContractExpiryEvents } from "../../../services/calendarService";

// ✅ 部門下拉：選項 + 依登入者預設
import { DEPARTMENTS } from "../../../constants/departments";
import { isSuperUser, getDeptCodeByEmail } from "../../../config/roles";

interface Props {
  initialData: any | null;
  onClose: () => void;
  onSuccess: () => void;
  isLegacyMode?: boolean;
}

// ✅ FIX：你的 ContractDoc 型別目前沒有宣告 dept，
// 但資料實際需要存 dept。用交集型別做「最小修正」避免去改全域型別檔。
type ContractFormState = Partial<ContractDoc> & { dept?: string };

function normalizeStatusCode(raw: any): "NOT_EFFECTIVE" | "ACTIVE" | "EXPIRED" | "TERMINATED" {
  if (!raw) return "ACTIVE";
  const s = String(raw).trim();
  const u = s.toUpperCase();

  if (s.includes("尚未生效") || s.includes("未生效") || s.includes("草稿")) return "NOT_EFFECTIVE";
  if (s.includes("生效中") || s.includes("有效")) return "ACTIVE";
  if (s.includes("已屆滿") || s.includes("已到期")) return "EXPIRED";
  if (s.includes("已解約") || s.includes("已終止")) return "TERMINATED";

  if (u === "NOT_EFFECTIVE" || u === "DRAFT") return "NOT_EFFECTIVE";
  if (u === "ACTIVE") return "ACTIVE";
  if (u === "EXPIRED") return "EXPIRED";
  if (u === "TERMINATED") return "TERMINATED";

  return "ACTIVE";
}

function buildInitialFormState(initialData: any | null): ContractFormState {
  const base: any =
    initialData ||
    ({
      status: "ACTIVE",
      attachments: [],
      milestones: [],
      related: { contractReviews: [], sealRequests: [], brandAssets: [], cases: [], disputes: [] },
    } as any);

  const next: any = { ...(base || {}) };
  // 兼容舊欄位命名
  if (!next.periodStart && next.effectiveDate) next.periodStart = next.effectiveDate;
  if (!next.signDate && next.signedDate) next.signDate = next.signedDate;
  if (!next.periodEnd && next.expiryDate) next.periodEnd = next.expiryDate;

  next.status = normalizeStatusCode(next.status);
  return next;
}

export function ContractForm({ initialData, onClose, onSuccess, isLegacyMode }: Props) {
  const { user } = useAuth();
  const { showToast } = useToast();

  const canPickDept = isSuperUser(user?.email);

  const [form, setForm] = useState<ContractFormState>(() => buildInitialFormState(initialData));

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!initialData && !form.contractNo) {
      fetchNextContractNo(!!isLegacyMode).then((no) => setForm((p) => ({ ...p, contractNo: no })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, isLegacyMode]);

  // ✅ FIX：非超管自動帶入 dept（避免 dept isolation 造成「存了卻看不到」）
  useEffect(() => {
    if (!user?.email) return;
    if (canPickDept) return; // 超管不強制
    if (form.dept) return;

    const deptCode = getDeptCodeByEmail(user.email);
    if (!deptCode) return;

    setForm((p) => ({ ...p, dept: deptCode }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, canPickDept]);

  const handleSave = async () => {
    const effective = (form as any).periodStart || (form as any).effectiveDate;
    if (!form.title || !form.counterparty || !effective) {
      return alert("請填寫必填欄位 (標題、對造、生效日)");
    }
    if (!form.dept) {
      return alert("請選擇部門");
    }

    setSaving(true);
    try {
      let finalAttachments = [...((form as any).attachments || [])];
      let docId = (initialData as any)?.id;

      // ✅ 正規化欄位：避免列表/行事曆讀不到（periodEnd vs expiryDate 等）
      const normalized: any = { ...(form as any) };
      normalized.status = normalizeStatusCode((form as any).status);
      normalized.periodStart = normalized.periodStart || normalized.effectiveDate;
      normalized.effectiveDate = normalized.periodStart;
      normalized.signDate = normalized.signDate || normalized.signedDate || normalized.periodStart;
      normalized.signedDate = normalized.signDate;
      normalized.periodEnd = normalized.periodEnd || normalized.expiryDate;
      normalized.expiryDate = normalized.periodEnd;

      // new doc: create first to get id
      if (!docId) {
        const ref = await db.collection("legal_contracts").add({
          ...normalized,
          dept: form.dept, // ✅ 確保寫入
          createdAt: serverTimestamp(),
          isLegacy: !!isLegacyMode,
          lastUpdatedBy: user?.displayName || "User",
          updatedAt: serverTimestamp(),
        });
        docId = ref.id;
      }

      // upload new files
      if (pendingFiles.length > 0) {
        const uploaded = await uploadContractFiles(docId, pendingFiles);
        finalAttachments = [...finalAttachments, ...uploaded];
      }

      const payload: any = {
        ...normalized,
        dept: form.dept, // ✅ 確保 update 也帶 dept
        attachments: finalAttachments,
        updatedAt: serverTimestamp(),
        lastUpdatedBy: user?.displayName || "User",
      };
      delete payload.id;

      await db.collection("legal_contracts").doc(docId).update(payload);

      // ✅ 行事曆同步（簽署日 + 到期日前 60/30/到期日）
      try {
        await syncContractExpiryEvents({
          id: docId,
          title: payload.title,
          contractNo: payload.contractNo,
          periodEnd: payload.periodEnd,
          signDate: payload.signDate || payload.periodStart,
          status: payload.status,
        });
      } catch (e) {
        // 行事曆同步失敗不阻斷主流程
        console.warn("[ContractForm] calendar sync failed", e);
      }

      showToast("存檔成功");
      onSuccess();
    } catch (e: any) {
      showToast("儲存失敗: " + (e?.message || "unknown"), "error");
    } finally {
      setSaving(false);
    }
  };

  const addMilestone = () =>
    setForm((p) => ({
      ...p,
      milestones: [...(((p as any).milestones || []) as any[]), { date: formatDate(new Date()), description: "" }],
    }));

  const addRelated = (key: any) => {
    const no = prompt("請輸入編號:");
    if (!no) return;
    setForm((p) => {
      const related = (p as any).related || {
        contractReviews: [],
        sealRequests: [],
        brandAssets: [],
        cases: [],
        disputes: [],
      };
      return { ...(p as any), related: { ...related, [key]: [...(related[key] || []), no] } };
    });
  };

  const renderRelatedBox = (label: string, key: any) => {
    const related = ((form as any).related || {}) as any;
    const arr = (related[key] || []) as string[];

    return (
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span className="label" style={{ marginBottom: 0 }}>
            {label}
          </span>
          <button className="smallbtn" style={{ fontSize: 10, padding: "2px 6px" }} onClick={() => addRelated(key)}>
            + 增加
          </button>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
          {arr.map((no, idx) => (
            <span key={idx} className="badge" style={{ background: "#333", color: "#fff", border: "none" }}>
              {no}{" "}
              <span
                style={{ cursor: "pointer", marginLeft: 4 }}
                onClick={() => {
                  const next = arr.filter((_, i) => i !== idx);
                  setForm((p) => ({
                    ...(p as any),
                    related: { ...(((p as any).related || {}) as any), [key]: next },
                  }));
                }}
              >
                ×
              </span>
            </span>
          ))}
          {arr.length === 0 && <span style={{ color: "#555", fontSize: 11 }}>無關聯</span>}
        </div>
      </div>
    );
  };

  return (
    <Modal title={initialData ? "編輯合約" : isLegacyMode ? "舊合約登錄" : "新增合約"} onClose={onClose} width={900}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div className="field">
          <label className="label">合約編號</label>
          <input
            className="input"
            value={(form as any).contractNo || ""}
            readOnly
            style={{ background: "rgba(255,255,255,0.05)", color: "#fbbf24" }}
          />
        </div>

        <div className="field">
          <label className="label">合約類型</label>
          <select
            className="input"
            value={(form as any).contractType || ""}
            onChange={(e) => setForm((p) => ({ ...p, contractType: e.target.value }))}
          >
            <option value="">請選擇</option>
            {CONTRACT_TYPE_OPTIONS.map((opt: any) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* ✅ 部門下拉 */}
        <div className="field">
          <label className="label">部門 *</label>
          <select
            className="input"
            value={form.dept || ""}
            onChange={(e) => setForm((p) => ({ ...p, dept: e.target.value }))}
            disabled={!canPickDept}
            style={!canPickDept ? { opacity: 0.85 } : undefined}
          >
            <option value="">請選擇</option>
            {DEPARTMENTS.map((d: any) => (
              <option key={d.code} value={d.code}>
                {d.name}
              </option>
            ))}
          </select>
          {!canPickDept && <div style={{ marginTop: 6, fontSize: 11, color: "#666" }}>（依登入者自動帶入，不可修改）</div>}
        </div>

        <div className="field" style={{ gridColumn: "1/-1" }}>
          <label className="label">合約名稱 *</label>
          <input
            className="input"
            value={(form as any).title || ""}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          />
        </div>

        <div className="field">
          <label className="label">相對人 (對造) *</label>
          <input
            className="input"
            value={(form as any).counterparty || ""}
            onChange={(e) => setForm((p) => ({ ...p, counterparty: e.target.value }))}
            placeholder="公司全銜"
          />
        </div>

        <div className="field">
          <DateField
            label="生效日期 *"
            value={parseDate((form as any).periodStart || null)}
            onChange={(d) =>
              setForm((p) => ({
                ...(p as any),
                periodStart: formatDate(d),
                effectiveDate: formatDate(d),
              }))
            }
          />
        </div>

        <div className="field">
          <DateField
            label="簽署日期"
            value={parseDate((form as any).signDate || null)}
            onChange={(d) =>
              setForm((p) => ({
                ...(p as any),
                signDate: formatDate(d),
                signedDate: formatDate(d),
              }))
            }
          />
        </div>

        <div className="field">
          <DateField
            label="到期日期"
            value={parseDate((form as any).periodEnd || null)}
            onChange={(d) =>
              setForm((p) => ({
                ...(p as any),
                periodEnd: formatDate(d),
                expiryDate: formatDate(d),
              }))
            }
          />
        </div>

        <div className="field" style={{ gridColumn: "1/-1" }}>
          <label className="label">狀態</label>
          <select
            className="input"
            value={normalizeStatusCode((form as any).status)}
            onChange={(e) => setForm((p) => ({ ...(p as any), status: e.target.value }))}
          >
            <option value="NOT_EFFECTIVE">尚未生效</option>
            <option value="ACTIVE">生效中</option>
            <option value="EXPIRED">已屆滿</option>
            <option value="TERMINATED">已解約</option>
          </select>
        </div>

        <div className="field" style={{ gridColumn: "1/-1" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <label className="label" style={{ marginBottom: 0 }}>
              重要時間點
            </label>
            <button className="smallbtn" style={{ fontSize: 11 }} onClick={addMilestone}>
              + 新增點位
            </button>
          </div>

          {(((form as any).milestones || []) as any[]).map((m: any, i: number) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input
                type="date"
                className="input"
                style={{ width: 160 }}
                value={m.date || ""}
                onChange={(e) => {
                  const n = [...(((form as any).milestones || []) as any[])];
                  n[i] = { ...n[i], date: e.target.value };
                  setForm((p) => ({ ...(p as any), milestones: n }));
                }}
              />
              <input
                className="input"
                placeholder="說明"
                value={m.description || ""}
                onChange={(e) => {
                  const n = [...(((form as any).milestones || []) as any[])];
                  n[i] = { ...n[i], description: e.target.value };
                  setForm((p) => ({ ...(p as any), milestones: n }));
                }}
              />
            </div>
          ))}
        </div>

        <div
          className="field"
          style={{ gridColumn: "1/-1", background: "rgba(255,255,255,0.02)", padding: 12, borderRadius: 8 }}
        >
          <div style={{ fontWeight: "bold", fontSize: 13, color: "#60a5fa", marginBottom: 12 }}>系統關聯編號</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              {renderRelatedBox("關聯合約審理 (CR-)", "contractReviews")}
              {renderRelatedBox("關聯用印申請 (SEAL-)", "sealRequests")}
            </div>
            <div>
              {renderRelatedBox("關聯品牌資產 (BA-)", "brandAssets")}
              {renderRelatedBox("關聯案件 (CASE-)", "cases")}
              {renderRelatedBox("關聯爭議 (DIS-)", "disputes")}
            </div>
          </div>
        </div>

        <div className="field" style={{ gridColumn: "1/-1" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <label className="label" style={{ marginBottom: 0 }}>
              內容摘要 (AI 輔助)
            </label>
            <AIPlaceholderButton type="summary" />
          </div>
          <textarea
            className="input"
            rows={4}
            value={(form as any).notes || ""}
            onChange={(e) => setForm((p) => ({ ...(p as any), notes: e.target.value }))}
          />
        </div>
      </div>

      <FileUploadKit
        label="合約附件"
        existingFiles={(form as any).attachments}
        rawFiles={pendingFiles}
        onFilesSelected={setPendingFiles}
        onRemoveRawFile={(idx) => setPendingFiles((prev) => prev.filter((_, i) => i !== idx))}
        onRemoveFile={(_f: any, i: number) => {
          const next = [...(((form as any).attachments) || [])];
          next.splice(i, 1);
          setForm((p) => ({ ...(p as any), attachments: next }));
        }}
        disabled={saving}
      />

      <div className="row" style={{ marginTop: 24, justifyContent: "flex-end" }}>
        <button className="btn" style={{ width: "auto" }} onClick={handleSave} disabled={saving}>
          {saving ? "處理中..." : "確認存檔"}
        </button>
      </div>
    </Modal>
  );
}
